import { createAsyncThunk } from '@reduxjs/toolkit';
import { gql } from '@apollo/client';
import { appServiceLines, commonEnums } from '@codametrix/ui-common';
import { getServiceDeskClient } from '../clients/service-desk.client';

// The BFF stores the BSP / "Bedside Pro" service line under the canonical key 'IPC'
// (see service-desk-directory-service migration WORKQ-687-2). The picker emits user-facing
// names. Collapse to the backend's canonical token before issuing the GraphQL filter.
const toBackendServiceLine = (service: string): string =>
  appServiceLines.isIPC(service) ? commonEnums.ServiceLines.IPC : service;

export type SubviewTabKey =
  | 'assigned'
  | 'holdsAndRequests'
  | 'submittedThisMonth';

export interface CoderQueueItem {
  id: string;
  queueName: string;
  totalInQueue: number;
  criticalAge: number;
}

export interface CoderTickerStats {
  submitted: number;
  excluded: number;
  holdByCoder: number;
  providerHolds: number;
  managerHolds: number;
  over30Days: number;
}

export interface CoderWorkitem {
  id: string;
  [key: string]: any;
}

// Group (service-line / health-system) metrics powering the right-sidebar cards.
export interface CoderGroupMetrics {
  casesOnHold: number;
  escalatedCases: number;
  completedToday: number;
}

// Result of claiming the next case in a queue ("Start Coding").
export interface ClaimNextCaseResult {
  claimed: boolean;
  caseId?: string; // cmx_case_uid
  processId?: string; // cmx_process_id
  service?: string;
  message?: string;
}

export interface SubviewWorkitemPage {
  tab: SubviewTabKey;
  items: CoderWorkitem[];
  total: number;
}

export interface SubviewWorkitemArgs {
  tab: SubviewTabKey;
  service: string;
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  filter?: string;
  sort?: string;
}

// GetAssignedProfiles returns only the queues the current coder is assigned to
// (scoped via WORKER_COLLECTION server-side). Same shape as GetPriorityProfiles:
// each entry in `metrics` is a QueueMetric key/value pair — { metric_key, value } —
// looked up by metric_key ('total_in_queue' / 'critically_aged'); `value` is a
// String, defaulted to 0 when the metric is absent.
const GET_ASSIGNED_QUEUES = gql`
  query GetAssignedProfiles($pageNumber: Int, $pageSize: Int, $service: String) {
    GetAssignedProfiles(pageNumber: $pageNumber, pageSize: $pageSize, service: $service) {
      data {
        collection_id
        name
        metrics {
          metric_key
          value
        }
      }
      total
    }
  }
`;

// Per-coder ticker stats reuse the existing BFF `productivityNumbers` operation
// (service-desk-bff-service, workitems module). It returns a scalar JSON blob keyed
// by the BFF's own field names, scoped to the logged-in coder via auth context.
const GET_CODER_PRODUCTIVITY = gql`
  query GetCoderProductivity($currentDate: String, $service: String) {
    productivityNumbers(currentDate: $currentDate, service: $service)
  }
`;

// Subview tabs reuse the existing BFF `SearchWorkItems` mutation. It forwards the
// request to the directory-service entries `/search` endpoint and returns
// workItemEntry { data: JSON, total: Int }. Filters are { key, operation, value }
// rules combined by `combinator`; the special value 'MY_CASES' is resolved
// server-side to the current coder's owner id.
const SEARCH_CODER_WORKITEMS = gql`
  mutation SearchCoderWorkitems($request: SearchWorkItemRequest!) {
    SearchWorkItems(request: $request) {
      data
      total
    }
  }
`;

// Group metrics (Cases on Hold / Escalated / Completed Today) come from the BFF
// GetGroupMetrics op as { metric_key, value } pairs under data[]. The default
// group bucket is 'profile_group' (service-desk-directory-service metric configs).
const GROUP_METRICS_KEY = 'profile_group';
const GET_GROUP_METRICS = gql`
  query GetGroupMetrics($groupKey: String, $service: String) {
    GetGroupMetrics(groupKey: $groupKey, service: $service) {
      data {
        metric_key
        value
      }
      total
    }
  }
`;

// "Start Coding" — claims the next unassigned case in a queue and returns the
// identifiers needed to open the coding bench (service-desk-bff ClaimNextCase).
const CLAIM_NEXT_CASE = gql`
  mutation ClaimNextCase($collectionId: String!, $service: String) {
    ClaimNextCase(collectionId: $collectionId, service: $service) {
      claimed
      cmx_case_uid
      cmx_process_id
      service
      message
    }
  }
`;

const pickMetric = (metrics: any[], key: string): any =>
  Array.isArray(metrics)
    ? metrics.find(m => m?.metric_key === key)
    : undefined;

const toNumber = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const fetchAssignedQueues = createAsyncThunk<
  CoderQueueItem[],
  { service: string }
>('coderView/fetchAssignedQueues', async ({ service }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  const { data } = await client.apolloClient.query({
    query: GET_ASSIGNED_QUEUES,
    variables: {
      service: toBackendServiceLine(service),
      pageNumber: 1,
      pageSize: 500
    },
    fetchPolicy: 'network-only'
  });
  const items: any[] = data?.GetAssignedProfiles?.data ?? [];
  return items.map(p => {
    const metrics: any[] = p?.metrics ?? [];
    return {
      id: p.collection_id,
      queueName: p.name,
      totalInQueue: toNumber(pickMetric(metrics, 'total_in_queue')?.value),
      criticalAge: toNumber(pickMetric(metrics, 'critically_aged')?.value)
    };
  });
});

const fetchTickerStats = createAsyncThunk<
  CoderTickerStats,
  { service: string }
>('coderView/fetchTickerStats', async ({ service }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  // Zero the time to local midnight before converting to ISO string so the
  // ticker tiles reset daily, mirroring the V1 Productivity Tracker widget
  // (packages/service-desk/src/components/wrappers/Worklist.tsx).
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  const { data } = await client.apolloClient.query({
    query: GET_CODER_PRODUCTIVITY,
    variables: {
      service: toBackendServiceLine(service),
      currentDate: currentDate.toISOString()
    },
    fetchPolicy: 'network-only'
  });
  const stats = data?.productivityNumbers ?? {};
  return {
    submitted: toNumber(stats.casesSubmittedToday),
    excluded: toNumber(stats.casesExcludedToday),
    holdByCoder: toNumber(stats.holdByCoder),
    providerHolds: toNumber(stats.holdForDocumentationReview),
    managerHolds: toNumber(stats.holdForManagerReview),
    over30Days: toNumber(stats.agedCasesNumber)
  };
});

// Active (not-yet-submitted) work the coder is responsible for.
const ASSIGNED_CASE_STATUSES = ['NEW', 'IN_PROGRESS', 'UPDATED'];
// Coder-, manager- and provider/documentation-review holds (see the BFF
// productivityNumbers hold queries for the canonical hold_status tokens).
const HOLD_STATUSES = [
  'HOLD',
  'HOLD_FOR_MANAGER_REVIEW',
  'HOLD_FOR_DOCUMENTATION_QUERY'
];
const SUBMITTED_CASE_STATUSES = ['SUBMITTED', 'SENT'];

const startOfMonthISO = (): string => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
};

// Build the per-tab filter rules. Every tab is scoped to the current coder via the
// server-resolved 'MY_CASES' owner token and to the active service line.
const buildTabFilters = (
  tab: SubviewTabKey,
  backendService: string,
  search?: string
): any[] => {
  const filters: any[] = [
    { key: 'service', operation: 'eq', value: backendService },
    { key: 'owner', operation: 'eq', value: 'MY_CASES' }
  ];

  switch (tab) {
    case 'assigned':
      filters.push({
        key: 'case_status',
        operation: 'in',
        value: ASSIGNED_CASE_STATUSES
      });
      break;
    case 'holdsAndRequests':
      filters.push({
        key: 'hold_status',
        operation: 'in',
        value: HOLD_STATUSES
      });
      break;
    case 'submittedThisMonth':
      filters.push({
        key: 'case_status',
        operation: 'in',
        value: SUBMITTED_CASE_STATUSES
      });
      // NOTE: confirm the date column/operator against an existing SearchWorkItems
      // request during verification; submitted_date is the WorkItemsInput field.
      filters.push({
        key: 'submitted_date',
        operation: 'gte',
        value: startOfMonthISO()
      });
      break;
  }

  // Best-effort free-text search on patient name (the BFF special-cases the
  // patient_name startsWith filter). Extend with more columns if needed.
  if (search) {
    filters.push({
      key: 'patient_name',
      operation: 'startsWith',
      value: search
    });
  }

  return filters;
};

const fetchSubviewWorkitems = createAsyncThunk<
  SubviewWorkitemPage,
  SubviewWorkitemArgs
>('coderView/fetchSubviewWorkitems', async args => {
  const { tab, service, pageNumber, pageSize, search, sort } = args;
  const client = getServiceDeskClient();
  await client.refreshToken();
  const limit = pageSize ?? 50;
  const offset = ((pageNumber ?? 1) - 1) * limit;
  const { data } = await client.apolloClient.mutate({
    mutation: SEARCH_CODER_WORKITEMS,
    variables: {
      request: {
        combinator: 'and',
        filters: buildTabFilters(tab, toBackendServiceLine(service), search),
        pagination: {
          limit,
          offset,
          order: 'asc',
          sort: sort || 'date_of_service'
        }
      }
    },
    fetchPolicy: 'network-only'
  });
  const items: CoderWorkitem[] = data?.SearchWorkItems?.data ?? [];
  return {
    tab,
    items,
    total: data?.SearchWorkItems?.total ?? items.length
  };
});

const fetchGroupMetrics = createAsyncThunk<
  CoderGroupMetrics,
  { service: string }
>('coderView/fetchGroupMetrics', async ({ service }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  const { data } = await client.apolloClient.query({
    query: GET_GROUP_METRICS,
    variables: {
      groupKey: GROUP_METRICS_KEY,
      service: toBackendServiceLine(service)
    },
    fetchPolicy: 'network-only'
  });
  const metrics: any[] = data?.GetGroupMetrics?.data ?? [];
  return {
    casesOnHold: toNumber(pickMetric(metrics, 'cases_on_hold')?.value),
    escalatedCases: toNumber(pickMetric(metrics, 'escalated_cases')?.value),
    // No `completed_today` metric exists; submitted_today is the closest signal.
    completedToday: toNumber(pickMetric(metrics, 'submitted_today')?.value)
  };
});

const claimNextCase = createAsyncThunk<
  ClaimNextCaseResult,
  { collectionId: string; service: string }
>('coderView/claimNextCase', async ({ collectionId, service }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  const { data } = await client.apolloClient.mutate({
    mutation: CLAIM_NEXT_CASE,
    variables: { collectionId, service: toBackendServiceLine(service) }
  });
  const result = data?.ClaimNextCase ?? {};
  return {
    claimed: !!result.claimed,
    caseId: result.cmx_case_uid ?? undefined,
    processId: result.cmx_process_id ?? undefined,
    service: result.service ?? undefined,
    message: result.message ?? undefined
  };
});

export {
  fetchAssignedQueues,
  fetchTickerStats,
  fetchSubviewWorkitems,
  fetchGroupMetrics,
  claimNextCase
};
